import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Form, Input, Modal, Select, message } from 'antd';
import { getCredPayoutAccount, saveCredPayoutAccount } from 'api/credentialing';
import type { ProviderCredentialingPayoutAccountDto } from 'api/credentialing/types';
import AutoAddressInput from 'components/AutoAddressInput';
import FileUploadPrivate from 'components/form/FileUploadPrivate';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import StateSelect from 'components/form/StateSelect';
import { fileNameFromKey } from 'utils/file';
import s from './s.module.less';

// Credentialing 收款账户（保险报销打款）表单：字段对齐后端独立 DTO
// （accountHolder / bankName / accountType CHECKING|SAVINGS / routingNumber 9 位 / accountNumber）。
// 与 api/paymentSetup 的 PayoutAccount 是两套接口，不复用。
// 字段集合与弹窗分开导出：submitted 页用弹窗，Settings 的 Payout account tab 行内复用字段集合。

const digitsOnly = (max: number) => (e: React.ChangeEvent<HTMLInputElement>) =>
    e.target.value.replace(/\D/g, '').slice(0, max);

const ZIP_RULES = [
    { required: true, message: 'Enter the ZIP code' },
    { pattern: /^\d{5}$/, message: 'ZIP code is 5 digits' },
];

// 表单默认值：持有人地址默认同 practice 地址（原型 holderSame 默认 true）
export const PAYOUT_FORM_DEFAULTS: ProviderCredentialingPayoutAccountDto = {
    accountType: 'CHECKING',
    holderSameAsPractice: true,
};

// 保存是整份覆盖（没传的字段会被清空）：以已存的完整 detail 为底，叠上表单值；
// 勾了 Same as practice 时 holder* 传 null；证明文件名从 key 推出
export const buildPayoutDto = (
    base: ProviderCredentialingPayoutAccountDto | null | undefined,
    values: ProviderCredentialingPayoutAccountDto,
): ProviderCredentialingPayoutAccountDto => {
    const dto: ProviderCredentialingPayoutAccountDto = { ...(base || {}), ...values };
    if (dto.holderSameAsPractice) {
        dto.holderStreet = null;
        dto.holderCity = null;
        dto.holderState = null;
        dto.holderZip = null;
    }
    dto.proofFileName = dto.proofFileUrl ? fileNameFromKey(dto.proofFileUrl) : null;
    return dto;
};

// 回填：只放有值的字段（后端 null 直接 setFieldsValue 会把已输入的清空）
export const filledPayoutValues = (dto?: ProviderCredentialingPayoutAccountDto | null) =>
    Object.fromEntries(Object.entries(dto || {}).filter(([, v]) => v !== null && v !== undefined && v !== ''));

const WarnIcon = () => (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="#F59E0B" aria-hidden>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 8a1 1 0 100-2 1 1 0 000 2z"
        />
    </svg>
);

// 街道地址走 Google 自动补全（项目约定），选中建议后回填同组的 City / State / ZIP。
// setFieldValue 不触发 Form 的 onValuesChange，回填后用 onAutoFill 通知宿主同步（payer 链的 payout 步骤靠它）
const StreetAutoFill = ({
    fields,
    label,
    requiredMessage,
    onAutoFill,
}: {
    fields: { street: string; city: string; state: string; zip: string };
    label: string;
    requiredMessage: string;
    onAutoFill?: () => void;
}) => {
    const form = Form.useFormInstance();
    const [setting] = useSettings();
    const fill = (name: string, val: string) => {
        form.setFieldValue(name, val);
        onAutoFill?.();
    };
    return (
        <Form.Item name={fields.street} label={label} rules={[{ required: true, message: requiredMessage }]}>
            <AutoAddressInput
                size="large"
                placeholder="Street address"
                displayFields={['street']}
                onCityChange={(val) => fill(fields.city, val)}
                onStateChange={(val) => {
                    const target = (setting[EnumFields.STATE] || []).find(
                        (item) => item.value === val || item.label === val,
                    );
                    fill(fields.state, target ? String(target.value) : val);
                }}
                onPostcodeChange={(val) => fill(fields.zip, val.replace(/\D/g, '').slice(0, 5))}
            />
        </Form.Item>
    );
};

// 09/24 起 payout 表单按 Medicare EFT 表（CMS 588）补齐：账户持有人地址 + 银行地址 + 账户证明。
// 原型 dashboard 弹窗、Settings、payer 链里的 payout 步骤是同一套字段（payoutComplete 同一口径），
// 银行证明文件不必填（原型允许先跳过）
export const PayoutAccountFields = ({ onAutoFill }: { onAutoFill?: () => void } = {}) => (
    <>
        <div className={s.fieldRow}>
            <Form.Item name="bankName" label="Bank name" rules={[{ required: true, message: 'Enter the bank name' }]}>
                <Input size="large" placeholder="e.g. Chase" />
            </Form.Item>
            <Form.Item
                name="accountHolder"
                label="Account holder name"
                rules={[{ required: true, message: 'Enter the account holder name' }]}
            >
                <Input size="large" placeholder="Full name on the account" />
            </Form.Item>
        </div>
        <div className={s.fieldRow}>
            <Form.Item
                name="routingNumber"
                label="Routing number"
                getValueFromEvent={digitsOnly(9)}
                rules={[
                    { required: true, message: 'Enter the routing number' },
                    { pattern: /^\d{9}$/, message: 'Routing number is 9 digits' },
                ]}
            >
                <Input size="large" placeholder="9 digits" inputMode="numeric" maxLength={9} />
            </Form.Item>
            <Form.Item
                name="accountNumber"
                label="Account number"
                getValueFromEvent={digitsOnly(17)}
                rules={[
                    { required: true, message: 'Enter the account number' },
                    { pattern: /^\d{4,}$/, message: 'Account number is at least 4 digits' },
                ]}
            >
                <Input size="large" placeholder="Account number" inputMode="numeric" maxLength={17} />
            </Form.Item>
        </div>
        <div className={s.fieldRow}>
            <Form.Item name="accountType" label="Account type" rules={[{ required: true, message: 'Select a type' }]}>
                <Select
                    size="large"
                    options={[
                        { value: 'CHECKING', label: 'Checking' },
                        { value: 'SAVINGS', label: 'Savings' },
                    ]}
                />
            </Form.Item>
        </div>
        <div className={s.subTitle}>Account holder address</div>
        <Form.Item name="holderSameAsPractice" valuePropName="checked" className={s.sameAddr}>
            <Checkbox>Same as my practice address</Checkbox>
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(p, c) => p.holderSameAsPractice !== c.holderSameAsPractice}>
            {({ getFieldValue }) =>
                getFieldValue('holderSameAsPractice') ? null : (
                    <>
                        <StreetAutoFill
                            fields={{
                                street: 'holderStreet',
                                city: 'holderCity',
                                state: 'holderState',
                                zip: 'holderZip',
                            }}
                            label="Street address"
                            requiredMessage="Enter the street address"
                            onAutoFill={onAutoFill}
                        />
                        <div className={s.fieldRow3}>
                            <Form.Item
                                name="holderCity"
                                label="City"
                                rules={[{ required: true, message: 'Enter the city' }]}
                            >
                                <Input size="large" placeholder="City" />
                            </Form.Item>
                            <Form.Item
                                name="holderState"
                                label="State"
                                rules={[{ required: true, message: 'Select a state' }]}
                            >
                                <StateSelect size="large" placeholder="Select a state" />
                            </Form.Item>
                            <Form.Item
                                name="holderZip"
                                label="ZIP code"
                                getValueFromEvent={digitsOnly(5)}
                                rules={ZIP_RULES}
                            >
                                <Input size="large" placeholder="5 digits" inputMode="numeric" maxLength={5} />
                            </Form.Item>
                        </div>
                    </>
                )
            }
        </Form.Item>
        <div className={s.eftSection}>
            <div className={s.sectionTitle}>Medicare EFT details</div>
            <p className={s.sectionDesc}>
                The Medicare EFT form also asks for the address of the branch the account is held at, and for proof of
                the account.
            </p>
            <StreetAutoFill
                fields={{ street: 'bankStreet', city: 'bankCity', state: 'bankState', zip: 'bankZip' }}
                label="Bank street address"
                requiredMessage="Enter the bank street address"
                onAutoFill={onAutoFill}
            />
            <div className={s.fieldRow3}>
                <Form.Item name="bankCity" label="City" rules={[{ required: true, message: 'Enter the city' }]}>
                    <Input size="large" placeholder="City" />
                </Form.Item>
                <Form.Item name="bankState" label="State" rules={[{ required: true, message: 'Select a state' }]}>
                    <StateSelect size="large" placeholder="Select a state" />
                </Form.Item>
                <Form.Item name="bankZip" label="ZIP code" getValueFromEvent={digitsOnly(5)} rules={ZIP_RULES}>
                    <Input size="large" placeholder="5 digits" inputMode="numeric" maxLength={5} />
                </Form.Item>
            </div>
            <div className={s.subTitle}>Voided check or bank verification letter</div>
            <div className={s.warnBox}>
                <WarnIcon />
                <span>
                    Medicare checks the address your bank has on file against your practice address. If they do not
                    match, ask your bank to update it before you submit, or the application comes back.
                </span>
            </div>
            <Form.Item name="proofFileUrl" className={s.proofUpload}>
                <FileUploadPrivate uploadType="LICENSE" accept=".pdf,.jpg,.jpeg,.png" tips="PNG, JPG, PDF up to 10MB" />
            </Form.Item>
        </div>
    </>
);

interface ModalProps {
    open: boolean;
    onClose: () => void;
    // 保存成功后回调（submitted 页据此把行翻成 Added）
    onSaved?: () => void;
}

// "Set up your payout account" 弹窗（原型 #submitted 的 payout setup，AntD v5 风格）
export const CredentialingPayoutModal = ({ open, onClose, onSaved }: ModalProps) => {
    const [form] = Form.useForm<ProviderCredentialingPayoutAccountDto>();
    const [saving, setSaving] = useState(false);
    // 已存的完整账户：保存时以它为底整份提交（接口整份覆盖）
    const baseRef = useRef<ProviderCredentialingPayoutAccountDto | null>(null);

    // 打开时回填已保存的账户（编辑场景）
    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res: any = await getCredPayoutAccount();
                const dto = res?.data?.data;
                if (cancelled || !dto) return;
                baseRef.current = dto;
                // 只回填有值的字段：后端未设置的字段是 null，直接 setFieldsValue 会把
                // 用户在 GET 返回前已输入的内容清空（打开弹窗立刻打字就会踩中）
                const filled = filledPayoutValues(dto);
                if (Object.keys(filled).length) form.setFieldsValue(filled);
            } catch (e) {
                // ignore：无已存账户按空表单处理
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, form]);

    const handleSave = async () => {
        try {
            await form.validateFields();
        } catch {
            return;
        }
        setSaving(true);
        try {
            const res: any = await saveCredPayoutAccount(buildPayoutDto(baseRef.current, form.getFieldsValue()));
            if (res?.error) {
                message.error(res.error || 'Failed to save');
                return;
            }
            message.success('Payout account added. Reimbursements will be deposited to this account.');
            onSaved?.();
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            title="Set up your payout account"
            width={665}
            destroyOnClose
            footer={[
                <Button key="cancel" size="large" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>,
                <Button key="save" size="large" type="primary" loading={saving} onClick={handleSave}>
                    Save
                </Button>,
            ]}
        >
            <p className={s.modalDesc}>
                Add the bank account where your insurance reimbursements will be deposited once you&apos;re
                credentialed. Payers pay this account <b>directly</b>, and your bank details are encrypted and never
                shared.
            </p>
            <Form form={form} layout="vertical" initialValues={PAYOUT_FORM_DEFAULTS}>
                <PayoutAccountFields />
            </Form>
        </Modal>
    );
};

export default CredentialingPayoutModal;
